import type { IService } from '../../shared';
import type { ServiceManager } from '../service-manager';
import { GlyphCache } from './pipeline/glyph/cache';
import { TextRenderer } from './renderer';
import type { ITextRenderer, TextRendererOptions } from './types';

/**
 * 创建并统一释放相互隔离的 Text Renderer。
 */
export class TextRenderService implements IService {
    /**
     * 当前编辑器实例内所有 Renderer 共享的位置无关字形模板缓存。
     *
     * Cache 自行按 Engine identity 与 font revision 失效；Renderer 只获得同一实例引用，不拥有其
     * 生命周期，也不能在单个 Renderer dispose 时清空其他图层仍可复用的模板。
     */
    private readonly glyphCache = new GlyphCache();

    private readonly renderers = new Set<TextRenderer>();

    /**
     * 创建 framework Text Render Service。
     *
     * @param services - 当前 framework 的 service manager。
     * @returns TextRenderService 实例。
     */
    constructor(private readonly services: ServiceManager) {}

    /**
     * 接入 framework 初始化生命周期。
     *
     * @returns void。
     */
    onInit(): void {}

    /**
     * 创建由单个真实宿主持有的隔离 renderer。
     *
     * 新 Renderer 获得私有 Pipeline，同时注入 Service 级 GlyphCache。共享范围只到当前编辑器实例，
     * 不跨 Framework 或文档生命周期。
     *
     * @param options - 同步调用的后台依赖使结果失效时使用的 renderer 级通知选项。
     * @returns 新创建的 TextRenderer。
     */
    createRenderer(options: TextRendererOptions): ITextRenderer {
        const renderer = new TextRenderer(
            {
                font: this.services.font,
                loading: this.services.loading,
                textEngine: this.services.textEngine,
                textRender: this,
                glyphCache: this.glyphCache,
            },
            options,
        );
        this.renderers.add(renderer);
        return renderer;
    }

    /**
     * 移除已释放的 renderer。
     *
     * @param renderer - 已释放的 renderer 实例。
     * @returns void。
     */
    releaseRenderer(renderer: TextRenderer): void {
        this.renderers.delete(renderer);
    }

    /**
     * 在 framework 卸载时释放全部活跃 renderer。
     *
     * Renderer dispose 会释放各自常驻 TextBuffer；全部 Renderer 结束后再清空共享 GlyphCache，避免
     * 模板引用跨越编辑器生命周期。
     *
     * @returns void。
     */
    onUnmount(): void {
        for (const renderer of [...this.renderers]) renderer.dispose();
        this.glyphCache.clear();
    }
}
